import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /**
   * @type {number | null}
   */
  #submenuHeightFrame = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('scroll', this.#queueActiveSubmenuHeightSync, { passive: true, capture: true });
    window.visualViewport?.addEventListener('resize', this.#queueActiveSubmenuHeightSync);
    window.visualViewport?.addEventListener('scroll', this.#queueActiveSubmenuHeightSync);
    document.fonts?.addEventListener('loadingdone', this.#queueActiveSubmenuHeightSync);
    this.#connectOverflowMenu();
    customElements.whenDefined('overflow-list').then(() => {
      if (!this.isConnected) return;
      this.#connectOverflowMenu();
      const submenu = findSubmenu(this.#state.activeItem);
      if (submenu) this.#observeSubmenuSize(submenu);
      this.#queueActiveSubmenuHeightSync();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('scroll', this.#queueActiveSubmenuHeightSync, { capture: true });
    window.visualViewport?.removeEventListener('resize', this.#queueActiveSubmenuHeightSync);
    window.visualViewport?.removeEventListener('scroll', this.#queueActiveSubmenuHeightSync);
    document.fonts?.removeEventListener('loadingdone', this.#queueActiveSubmenuHeightSync);
    this.refs.overflowMenu?.shadowRoot?.removeEventListener('slotchange', this.#queueActiveSubmenuHeightSync);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#clearDeactivateTimer();
    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();
    this.#cleanupQueuedHeightSync();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#syncActiveSubmenuHeight();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  #connectOverflowMenu() {
    // slotchange stays inside the shadow root, including when items move to More.
    this.refs.overflowMenu?.shadowRoot?.addEventListener('slotchange', this.#queueActiveSubmenuHeightSync);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Keep desktop dropdowns centered in the viewport, not just under the nav item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} submenu
   */
  #positionSubmenu(item, submenu) {
    if (!submenu) return;

    const isDefaultSlot = item.parentElement?.slot === '';

    if (!isDefaultSlot) {
      submenu.style.removeProperty('--submenu-viewport-center-offset');
      return;
    }

    const itemRect = item.getBoundingClientRect();
    const itemCenter = itemRect.left + itemRect.width / 2;
    const viewportCenter = document.documentElement.clientWidth / 2;
    submenu.style.setProperty('--submenu-viewport-center-offset', `${viewportCenter - itemCenter}px`);
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#clearDeactivateTimer();
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item) return;

    if (item == this.#state.activeItem) {
      this.#queueActiveSubmenuHeightSync();
      return;
    }

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      this.#positionSubmenu(item, submenu);

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
      this.#observeSubmenuSize(submenu);

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        this.#queueActiveSubmenuHeightSync();
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      this.#queueActiveSubmenuHeightSync();
    }

    this.#syncActiveSubmenuHeight();
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
    this.#queueActiveSubmenuHeightSync();
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      this.#clearDeactivateTimer();
      return;
    }

    this.#scheduleDeactivate();
  }

  /**
   * Deactivate the active item after a short grace period so the pointer can move
   * from the top-level link into the absolutely positioned submenu.
   * @param {HTMLElement | null} [item]
   */
  #scheduleDeactivate(item = this.#state.activeItem) {
    this.#clearDeactivateTimer();
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivate(item);
    }, 180);
  }

  #clearDeactivateTimer() {
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    const submenu = findSubmenu(item);
    if (
      item.matches(':focus') ||
      submenu?.matches(':hover, :focus-within') ||
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover')
    ) {
      return;
    }

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
    this.#cleanupResizeObserver();
    this.#cleanupMutationObserver();
    this.#cleanupQueuedHeightSync();
  };

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Keep the open menu height aligned with its rendered content.
   */
  #syncActiveSubmenuHeight = () => {
    if (!this.#state.activeItem || !this.headerComponent) return;

    const item = this.#state.activeItem;
    const isDefaultSlot = item.parentElement?.slot === '';
    const submenu = findSubmenu(item);
    const panel = isDefaultSlot ? submenu : this.overflowMenu;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (submenu) {
      this.#positionSubmenu(item, submenu);
    }

    if (panel) {
      // Header height alone excludes announcements, row padding and scroll position.
      // Constrain the scroller to the space below the panel's actual screen position.
      const viewport = window.visualViewport;
      const viewportBottom = viewport ? viewport.offsetTop + viewport.height : document.documentElement.clientHeight;
      const availableHeight = Math.max(0, Math.floor(viewportBottom - panel.getBoundingClientRect().top));
      this.style.setProperty('--submenu-available-height', `${availableHeight}px`);
    }

    const finalHeight = measureMenuHeight(panel);

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
  };

  /**
   * Coalesce layout updates for the entire time a menu is open. No retry window
   * can reliably predict when images, fonts or header content finish loading.
   */
  #queueActiveSubmenuHeightSync = () => {
    if (!this.#state.activeItem || this.#submenuHeightFrame !== null) return;
    this.#submenuHeightFrame = requestAnimationFrame(() => {
      this.#submenuHeightFrame = null;
      this.#syncActiveSubmenuHeight();
    });
  };

  /**
   * Watch the active submenu for layout changes caused by responsive wrapping or deferred content.
   * @param {HTMLElement} submenu
   */
  #observeSubmenuSize(submenu) {
    this.#cleanupResizeObserver();
    if (!('ResizeObserver' in window)) return;

    this.#submenuResizeObserver = new ResizeObserver(() => {
      this.#queueActiveSubmenuHeightSync();
    });
    this.#submenuResizeObserver.observe(submenu);
    const submenuInner = submenu.querySelector('.menu-list__submenu-inner');
    if (submenuInner instanceof HTMLElement) {
      this.#submenuResizeObserver.observe(submenuInner);
    }

    // Changes to the header can move an unchanged submenu. Observe its layout
    // ancestors as well as the panel, including announcement rows above it.
    for (let ancestor = this.parentElement; ancestor; ancestor = ancestor.parentElement) {
      this.#submenuResizeObserver.observe(ancestor);
      if (ancestor.id === 'header-group') break;
    }
    for (const element of this.headerComponent?.querySelectorAll('.header__row, .header__columns') ?? []) {
      this.#submenuResizeObserver.observe(element);
    }
    if (this.overflowMenu) this.#submenuResizeObserver.observe(this.overflowMenu);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  #cleanupResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }

  #cleanupQueuedHeightSync() {
    if (this.#submenuHeightFrame !== null) {
      cancelAnimationFrame(this.#submenuHeightFrame);
      this.#submenuHeightFrame = null;
    }
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Measure an element's rendered height with a small ceiling buffer for fractional pixels.
 * @param {HTMLElement | null} element
 * @returns {number}
 */
function measureMenuHeight(element) {
  if (!element) return 0;

  return Math.ceil(Math.max(element.offsetHeight, element.getBoundingClientRect().height)) + 2;
}
